import type { BusPayload } from "./messages";

/**
 * Line codec — converts device wire lines to bus messages and back.
 * Mirrors Node-RED's serial-node parsing options, narrowed to the two
 * conventions Granrojo firmware uses:
 *
 *   kv     `CO2:512,T:24.1,RH:48`  → { co2: 512, t: 24.1, rh: 48 }
 *   json   `{"co2":512,"t":24.1}`  → same
 *
 * `auto` (default) tries json when the line starts with `{`, then kv,
 * then falls back to the raw line under the `line` key.
 */

export type ParseMode = "auto" | "kv" | "json" | "raw";

export interface DecodedLine {
  /** Signal key → payload; topic becomes `<device>/<key>`. */
  values: Record<string, BusPayload>;
  mode: "kv" | "json" | "raw";
}

function coerce(raw: string): BusPayload {
  const trimmed = raw.trim();
  if (trimmed === "true") return true;
  if (trimmed === "false") return false;
  if (trimmed !== "" && !Number.isNaN(Number(trimmed))) return Number(trimmed);
  return trimmed;
}

function decodeKv(line: string): Record<string, BusPayload> | null {
  const values: Record<string, BusPayload> = {};
  let found = false;
  for (const part of line.split(",")) {
    const idx = part.indexOf(":");
    if (idx <= 0) continue;
    const key = part.slice(0, idx).trim();
    if (!/^[A-Za-z_][A-Za-z0-9_]*$/.test(key)) continue;
    values[key.toLowerCase()] = coerce(part.slice(idx + 1));
    found = true;
  }
  return found ? values : null;
}

function decodeJson(line: string): Record<string, BusPayload> | null {
  try {
    const obj: unknown = JSON.parse(line);
    if (typeof obj !== "object" || obj === null || Array.isArray(obj)) return null;
    const values: Record<string, BusPayload> = {};
    for (const [key, value] of Object.entries(obj)) {
      values[key.toLowerCase()] = value as BusPayload;
    }
    return values;
  } catch {
    return null;
  }
}

export function decodeLine(line: string, mode: ParseMode = "auto"): DecodedLine {
  const trimmed = line.trim();
  if (mode === "json" || (mode === "auto" && trimmed.startsWith("{"))) {
    const json = decodeJson(trimmed);
    if (json) return { values: json, mode: "json" };
    if (mode === "json") return { values: { line: trimmed }, mode: "raw" };
  }
  if (mode === "kv" || mode === "auto") {
    const kv = decodeKv(trimmed);
    if (kv) return { values: kv, mode: "kv" };
  }
  return { values: { line: trimmed }, mode: "raw" };
}

/**
 * Encode an outbound command as a wire line — the kv codec in reverse:
 * publishing `{topic:"matrix/power", payload:true}` writes `POWER:1`.
 * String payloads that already contain `:` are passed through verbatim
 * so pages can send raw protocol lines via `<device>/$send`.
 */
export function encodeCommand(key: string, payload: BusPayload): string {
  const upper = key.toUpperCase();
  if (payload === true) return `${upper}:1`;
  if (payload === false) return `${upper}:0`;
  if (payload === null) return upper;
  if (typeof payload === "number") return `${upper}:${payload}`;
  if (typeof payload === "string") return `${upper}:${payload}`;
  return `${upper}:${JSON.stringify(payload)}`;
}

/** Incremental line splitter for stream chunks. */
export class LineSplitter {
  private buf = "";

  push(chunk: string): string[] {
    this.buf += chunk;
    const lines: string[] = [];
    let idx: number;
    while ((idx = this.buf.indexOf("\n")) >= 0) {
      lines.push(this.buf.slice(0, idx).replace(/\r$/, ""));
      this.buf = this.buf.slice(idx + 1);
    }
    return lines;
  }

  flush(): string {
    const rest = this.buf;
    this.buf = "";
    return rest;
  }
}
